import {
  ArrowLeft,
  EditIcon,
  SemanticBadge,
  Switch,
  ThemeColorNamesType,
  getColorByName,
} from "@bale/ui";
import {
  Close2,
  Edit,
  History,
  IconComponent,
  MidSync,
  Setting,
  Show,
  Tick,
} from "@bale/icons";
import { strings } from "intl/fa";
import React, { ReactComponentElement, ReactNode, useState } from "react";
import styles from "./CampaignItem.module.scss";
import sharedStyles from "../CampaignOrders.module.scss";
import {
  AdSpot,
  AdState,
  ApiCampaignData,
  CampaignState,
  CampaignType,
} from "../../../../../../../core/orderAd/orderAd.entity";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../../utils/store";
import {
  AppPage,
  NavigationActions,
} from "../../../../../../../core/navigation/navigation.slice";
import { OrdersListActions } from "../../../../../../../core/ordersList/ordersList.slice";
import { useTheme } from "@emotion/react";
import { useRequest } from "../../../../../../../hooks/useRequest";
import {
  changeCampaignStateThunk,
  getCampaignAdsThunk,
} from "../../../../../../../core/ordersList/ordersList.thunk";
import { ConfirmCampaignStateModal } from "./confirmCampaignStateModal/ConfirmCampaignStateModal";
import { getPagination } from "../../../../../../../utils/pagination";
import { toast } from "react-toastify";
import { useMyPanel } from "../../../../../../../hooks/useMyPanel";
import { isISOTimeAfterToday } from "utils/time";
import { CreditNotActiveModal } from "./creditNotActiveModal/CreditNotActiveModal";
import { digitsToHindi, toMoneyFormat } from "@bale/utils";
import { StopCampaignSettings } from "components/content/dialogAd/StopCampaignSettings";
import useIsLargeScreen from "../../../hooks/useIsDesktop";
import AdThumbnail from "../../AdThumbnail";

export const campaignLabels: {
  [key: string]: {
    label: string;
    color: ThemeColorNamesType;
    icon?: IconComponent;
    size?: number;
  };
} = {
  [CampaignState.Finished]: {
    label: strings.campaign_labels.Finished,
    color: "color_neutrals_n_300",
  },
  [CampaignState.AwaitingApproval]: {
    label: strings.campaign_labels.AwaitingApproval,
    color: "color_alternative_a_01",
    icon: MidSync,
    size: 16,
  },
  [CampaignState.Paused]: {
    label: strings.campaign_labels.Paused,
    color: "color_neutrals_n_300",
  },
  [CampaignState.Active]: {
    label: strings.campaign_labels.Active,
    color: "color_alternative_a_09",
    icon: Show,
    size: 16,
  },
  [CampaignState.Rejected]: {
    label: strings.campaign_labels.Rejected,
    color: "color_error",
    icon: Close2,
    size: 16,
  },
  [CampaignState.Canceled]: {
    label: strings.campaign_labels.Canceled,
    color: "color_alternative_a_04",
  },
  [CampaignState.Approved]: {
    label: strings.campaign_labels.Approved,
    color: "color_success",
    icon: Tick,
    size: 16,
  },
};

export const useOpenCampaignDetails = () => {
  const dispatch = useAppDispatch();
  return (campaign: ApiCampaignData) => {
    dispatch(OrdersListActions.setSelectedCampaign(campaign));
    dispatch(NavigationActions.setAppPage(AppPage.OrderDetail));
  };
};

interface Props {
  campaign: ApiCampaignData;
}

const CampaignItem: React.FC<React.PropsWithChildren<Props>> = ({
  campaign,
}) => {
  const [isActive, setIsActive] = useState(
    campaign.state === CampaignState.Active
  );
  const [showAreYouSureModal, setShowAreYouSureModal] = useState(false);
  const [showCreditNotActiveModal, setShowCreditNotActiveModal] =
    useState(false);
  const campaignRecord = campaign.ads[0];
  const { sendRequest: changeStateRequest, loading } = useRequest(
    changeCampaignStateThunk
  );
  const { sendRequest: getCampaigns } = useRequest(getCampaignAdsThunk);
  const selectCampaign = useOpenCampaignDetails();
  const myPanel = useMyPanel();
  const activeCampaigns = useAppSelector((state) =>
    state.ordersList.campaigns.filter(
      (campaign) => campaign.state === CampaignState.Active
    )
  );
  const isDesktop = useIsLargeScreen(1400);

  const changeCampaignState = (nextValue: boolean) => {
    changeStateRequest({
      campaign_id: campaign.id,
      state: nextValue ? CampaignState.Active : CampaignState.Paused,
      onSuccess: () => {
        getCampaigns(getPagination(1, 50));
        setShowAreYouSureModal(false);
        setIsActive((prevState) => !prevState);
      },
    });
  };

  const changeSwitchState = (nextValue: boolean) => {
    if (myPanel.credit <= 0) {
      toast.info(strings.campaign_state_modal.no_credit);
      return;
    }
    if (isISOTimeAfterToday(myPanel.credit_start_date)) {
      setShowCreditNotActiveModal(true);
      return;
    }

    if (
      activeCampaigns.length >= myPanel.allowed_concurrent_campaign &&
      nextValue
    ) {
      toast.info(
        strings.campaign_state_modal.no_more_actives.replace(
          "{value}",
          digitsToHindi(String(myPanel.allowed_concurrent_campaign))
        )
      );
    } else {
      setShowAreYouSureModal(true);
    }
  };

  const campaignIsStartable =
    campaign.state === CampaignState.Approved ||
    campaign.state === CampaignState.Active ||
    campaign.state === CampaignState.Paused;

  const goToStopCampaign = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    dispatch(OrdersListActions.setSelectedCampaign(campaign));
    dispatch(NavigationActions.setAppPage(AppPage.StopCampaign));
  };

  const dispatch = useAppDispatch();
  return (
    <>
      {isDesktop ? (
        <div
          className={sharedStyles.campaignItem}
          onClick={() => selectCampaign(campaign)}
        >
          <div className={sharedStyles.col} style={{ height: 50 }}>
            <AdThumbnail
              pic_url={campaignRecord.pic_url}
              spot={campaignRecord.spot}
              id={campaignRecord.ad_id}
            />
          </div>
          <p className={`${sharedStyles.col} ${styles.title}`}>
            {campaignRecord.title ? campaignRecord.title : "-"}
          </p>
          <p className={`${sharedStyles.col} ${styles.desc}`}>
            {campaignRecord.description}
          </p>
          <p className={sharedStyles.col}>
            <SemanticBadge
              color="color_neutrals_n_300"
              text={
                campaignRecord.spot === AdSpot.AdSpotSponsoredMessage
                  ? strings.ad_sale.ordered_ads_list.ad_type.sponsored_message
                  : strings.ad_sale.ordered_ads_list.ad_type.dialog
              }
            />
          </p>
          <div className={sharedStyles.col}>
            <SemanticBadge
              color={campaignLabels[campaign.state].color}
              text={campaignLabels[campaign.state].label}
              icon={campaignLabels[campaign.state].icon}
              iconSize={campaignLabels[campaign.state].size}
              className={styles.badge}
            />
          </div>

          <div className={`${styles.stopValue} ${sharedStyles.col}`}>
            <p>
              {digitsToHindi(
                toMoneyFormat(
                  campaignRecord.stop_value +
                    ` ${campaignRecord.type === CampaignType.ClickBased ? strings.common.click : strings.common.view}`
                )
              )}
            </p>
            <EditIcon
              size={16}
              color={getColorByName("color_fixed_primary")}
              onClick={goToStopCampaign}
            />
          </div>

          <p className={`${sharedStyles.col} ${sharedStyles.startCampaign}`}>
            <Switch
              disabled={!campaignIsStartable}
              onChange={changeSwitchState}
              checked={isActive}
              stopPropagation
            />
          </p>
          <div className={sharedStyles.col}>
            <ArrowLeft size={24} />
          </div>
        </div>
      ) : (
        <div
          className={sharedStyles.campaignCard}
          onClick={() => selectCampaign(campaign)}
        >
          <div className={sharedStyles.header}>
            <div className={sharedStyles.info}>
              <AdThumbnail
                pic_url={campaignRecord.pic_url}
                spot={campaignRecord.spot}
                id={campaignRecord.ad_id}
              />
              <div className={sharedStyles.texts}>
                <h2>
                  {campaignRecord.title
                    ? campaignRecord.title
                    : strings.campaign_details.without_title}
                </h2>
                <p>{campaignRecord.description}</p>
              </div>
            </div>
            <ArrowLeft size={24} />
          </div>

          <div className={sharedStyles.row}>
            <p>{strings.ad_sale.ordered_ads_list.startCampaign}:</p>

            <Switch
              disabled={!campaignIsStartable}
              onChange={changeSwitchState}
              checked={isActive}
              stopPropagation
            />
          </div>

          <div className={sharedStyles.row}>
            <p>{strings.stop_campaign.title}:</p>

            <div className={`${styles.stopValue} ${sharedStyles.col}`}>
              <p>
                {digitsToHindi(
                  toMoneyFormat(
                    campaignRecord.stop_value + ` ${strings.common.click}`
                  )
                )}
              </p>
              <EditIcon
                size={16}
                color={getColorByName("color_fixed_primary")}
                onClick={goToStopCampaign}
              />
            </div>
          </div>

          <div className={sharedStyles.row}>
            <p>{strings.ad_sale.ordered_ads_list.adStatus}:</p>

            <SemanticBadge
              color={campaignLabels[campaign.state].color}
              text={campaignLabels[campaign.state].label}
              icon={campaignLabels[campaign.state].icon}
              iconSize={campaignLabels[campaign.state].size}
              className={styles.badge}
            />
          </div>

          <div className={sharedStyles.row}>
            <p>{strings.ad_sale.ordered_ads_list.adSpot}:</p>
            <SemanticBadge
              color="color_neutrals_n_300"
              text={
                campaignRecord.spot === AdSpot.AdSpotSponsoredMessage
                  ? strings.ad_sale.ordered_ads_list.ad_type.sponsored_message
                  : strings.ad_sale.ordered_ads_list.ad_type.dialog
              }
            />
          </div>
        </div>
      )}
      {showAreYouSureModal && (
        <ConfirmCampaignStateModal
          onClose={() => {
            setShowAreYouSureModal(false);
          }}
          changeState={changeCampaignState}
          loading={loading}
          nextState={!isActive}
        />
      )}
      {showCreditNotActiveModal && (
        <CreditNotActiveModal
          onClose={() => {
            setShowCreditNotActiveModal(false);
          }}
        />
      )}
    </>
  );
};

export default CampaignItem;
